import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuth } from '../lib/auth-context';

/**
 * Gate wrapper for any route that requires an authenticated customer.
 *
 * Three branches:
 *   - `loading`        -> minimal spinner (avoids flashing /login on
 *     reload while /v1/portal/auth/me is still in flight)
 *   - `unauthenticated`-> redirect to /portal/login, preserving the
 *     requested path in `state.from` so LoginPage can bounce back
 *     after success
 *   - `authenticated`  -> render children
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  const { t } = useTranslation('banners');

  if (status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gray-50 dark:bg-gray-800">
        <div className="text-sm text-gray-500">{t('loading')}</div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    // OCP-264 - preserve the query string too. Deep-links like
    // /redeem?code=ORB-XXX rely on the search part surviving the
    // /login bounce; pathname-only loses it.
    const from = location.pathname + (location.search ?? '');
    return <Navigate to="/login" replace state={{ from }} />;
  }

  return <>{children}</>;
}
